/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import { useFormikContext } from 'formik';
import { ReactElement, useEffect } from 'react';
import { batch } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../../redux/slices/appSlice';
import { 
  selectNonPublicStudentScheduleFormViewModel,
  selectNonPublicStudentLoadingNonPublicStudent } from '../../../../../redux/slices/nonPublicStudentSlice';
import { NonPublicStudentScheduleFormViewModel } from '../../../../../types/NonPublicStudentScheduleFormViewModel';
import { NonPublicStudentScheduleModel } from '../../../../../types/NonPublicStudentScheduleModel';
import { PanelType } from '../../../../../types/PanelType';
import { FormDropdownChangeEventConfig } from '../../../../../types/propTypes/FormDropdownPropTypes';
import { SelectOption } from '../../../../../types/SelectOption';
import { NonPublicStudentEntryScheduleValidationSchema } from '../../../../../types/Validation/NonPublicStudentEntryScheduleValidationSchema';
import { isStateNonAdmin } from '../../../../../utilities/userUtilities';
import FormDropdown from '../../../../FormInputs/FormDropdown/FormDropdown';
import Panel from '../../../../Panel/Panel';
import './NPSE_AddCoursePanel.css';
import InfoPanel from '../../../../../components/InfoPanel/InfoPanel';
import { createNotification } from '../../../../../redux/slices/notificationSlice';
import NoCTECoursesMessage from './NoCTECoursesMessage';
const NPSE_AddCoursePanel = (): ReactElement => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<NonPublicStudentScheduleModel>();
  const dispatch = useAppDispatch();

  const formViewModel: NonPublicStudentScheduleFormViewModel = useAppSelector(
    selectNonPublicStudentScheduleFormViewModel
  );

  const selectedDistrict = useAppSelector(selectSelectedDistrict);

  const clearCourseCodeDependentFields = (): void => {
    batch(() => {
      setFieldValue('schoolIdentifier', '');
      setFieldTouched('schoolIdentifier', false);

      setFieldValue('staffMemberIdentifier', '');
      setFieldTouched('staffMemberIdentifier', false);

      setFieldValue('courseSectionIdentifier', '');
      setFieldTouched('courseSectionIdentifier', false);
    });
  };

  const clearSchoolDependentFields = (): void => {
    batch(() => {
      setFieldValue('staffMemberIdentifier', '');
      setFieldTouched('staffMemberIdentifier', false);

      setFieldValue('courseSectionIdentifier', '');
      setFieldTouched('courseSectionIdentifier', false);
    });
  };

  const clearTeacherDependentFields = (): void => {
    batch(() => {
      setFieldValue('courseSectionIdentifier', '');
      setFieldTouched('courseSectionIdentifier', false);
    });
  };

  const handleCourseCodeChange = (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    clearCourseCodeDependentFields();

    batch(() => {
      setFieldValue('courseTitle', props.optionText);
      setFieldValue('districtIdentifier', selectedDistrict.districtId);
      setFieldValue('districtName', selectedDistrict.name);
    });

    return Promise.resolve();
  };

  const handleCourseSectionChange = (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    setFieldValue('courseSectionDescription', props.optionText);

    return Promise.resolve();
  };

  const handleSchoolIdentifierChange = (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    clearSchoolDependentFields();

    setFieldValue('schoolName', props.optionText);

    return Promise.resolve();
  };

  const handleTeacherChange = (
    props: FormDropdownChangeEventConfig
  ): Promise<void> => {
    clearTeacherDependentFields();

    setFieldValue('staffMemberName', props.optionText);

    return Promise.resolve();
  };

  const getCourseSectionValues = (): SelectOption[] => {
    if (
      formViewModel.extendedCourseCodeOptions.length === 0 ||
      values.courseIdentifier === '' ||
      values.schoolIdentifier === '' ||
      values.staffMemberIdentifier === ''
    ) {
      return [];
    }

    return formViewModel.extendedCourseCodeOptions
      .filter((x) => x.value === values.courseIdentifier)[0]
      .extendedSchoolOptions.filter(
        (s) => s.value === values.schoolIdentifier
      )[0]
      .extendedTeacherOptions.filter(
        (t) => t.value === values.staffMemberIdentifier
      )[0].courseSectionOptions;
  };

  const getSchoolIdentifierValues = (): SelectOption[] => {
    if (
      formViewModel.extendedCourseCodeOptions.length === 0 ||
      values.courseIdentifier === ''
    ) {
      return [];
    }

    return formViewModel.extendedCourseCodeOptions.filter(
      (x) => x.value === values.courseIdentifier
    )[0].extendedSchoolOptions;
  };

  const getTeacherValues = (): SelectOption[] => {
    if (
      formViewModel.extendedCourseCodeOptions.length === 0 ||
      values.courseIdentifier === '' ||
      values.schoolIdentifier === ''
    ) {
      return [];
    }

    return formViewModel.extendedCourseCodeOptions
      .filter((x) => x.value === values.courseIdentifier)[0]
      .extendedSchoolOptions.filter(
        (s) => s.value === values.schoolIdentifier
      )[0].extendedTeacherOptions;
  };

  return (
  <>
    {
      formViewModel.extendedCourseCodeOptions.length === 0 &&
      <NoCTECoursesMessage />
    }

    <Panel panelType={PanelType.INFO} heading="Add a Course">
      <div className="field-row field-row-grid-1-1-1">
        <FormDropdown
          displayName="Course Code"
          field="courseIdentifier"
          validationSchema={NonPublicStudentEntryScheduleValidationSchema}
          onChangeEvent={handleCourseCodeChange}
          disabled={formViewModel.extendedCourseCodeOptions.length === 0 || isStateNonAdmin()}
          options={formViewModel.extendedCourseCodeOptions}
        />
        <FormDropdown
          displayName="School Identifier"
          field="schoolIdentifier"
          validationSchema={NonPublicStudentEntryScheduleValidationSchema}
          disabled={values.courseIdentifier === '' || isStateNonAdmin()}
          onChangeEvent={handleSchoolIdentifierChange}
          options={getSchoolIdentifierValues()}
        />
      </div>
      <div className="field-row field-row-grid-1-1-1">
        <FormDropdown
          displayName="Teacher"
          field="staffMemberIdentifier"
          validationSchema={NonPublicStudentEntryScheduleValidationSchema}
          disabled={values.schoolIdentifier === '' || isStateNonAdmin()}
          onChangeEvent={handleTeacherChange}
          options={getTeacherValues()}
        />
        <FormDropdown
          displayName="Course Section"
          field="courseSectionIdentifier"
          validationSchema={NonPublicStudentEntryScheduleValidationSchema}
          disabled={values.staffMemberIdentifier === '' || isStateNonAdmin()}
          onChangeEvent={handleCourseSectionChange}
          options={getCourseSectionValues()}
        />
        <FormDropdown
          displayName="Status"
          field="status"
          validationSchema={NonPublicStudentEntryScheduleValidationSchema}
          disabled={values.courseSectionIdentifier === '' || isStateNonAdmin()}
          options={formViewModel.statusOptions}
        />
      </div>
    </Panel>
</>
  );
};

export default NPSE_AddCoursePanel;

